/**
 * The two-sided marketplace flywheel (§4) with the unanswered-demand loop (§5) in the centre.
 * Pure inline SVG using design tokens, so it works in both themes and at any width.
 */
const STEPS = ["Data providers", "Valuable datasets", "Query coverage", "AI developers", "Queries", "Provider earnings", "Demonstrated demand"];

export function Flywheel({ className, steps = STEPS, center = ["Unanswered", "demand"] }: { className?: string; steps?: string[]; center?: string[] }) {
  const cx = 260, cy = 210, r = 160;
  const pts = steps.map((_, i) => {
    const a = -Math.PI / 2 + (i / steps.length) * Math.PI * 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), a };
  });
  return (
    <svg viewBox="0 0 520 420" className={className} role="img" aria-label={`Marketplace flywheel: ${steps.join(" → ")} → back to ${steps[0]}`}>
      <defs>
        <marker id="fw-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="var(--accent)" />
        </marker>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border-strong)" strokeDasharray="2 6" />
      {pts.map((p, i) => {
        const n = pts[(i + 1) % pts.length]!;
        // arc segment between nodes, shortened so arrowheads sit outside node pills
        const a0 = p.a + 0.22, a1 = n.a - 0.22 + (i === pts.length - 1 ? Math.PI * 2 : 0);
        const s = { x: cx + r * Math.cos(a0), y: cy + r * Math.sin(a0) };
        const e = { x: cx + r * Math.cos(a1), y: cy + r * Math.sin(a1) };
        return <path key={`arc-${i}`} d={`M${s.x},${s.y} A${r},${r} 0 0 1 ${e.x},${e.y}`} fill="none" stroke="var(--accent)" strokeWidth="1.5" markerEnd="url(#fw-arrow)" opacity="0.85" />;
      })}
      {pts.map((p, i) => {
        const w = Math.max(96, steps[i]!.length * 7.2 + 20);
        return (
          <g key={steps[i]}>
            <rect x={p.x - w / 2} y={p.y - 15} width={w} height="30" rx="6" fill="var(--panel-2)" stroke="var(--border-strong)" />
            <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="12" fill="var(--text)" fontFamily="var(--font-sans)">{steps[i]}</text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r="58" fill="var(--accent-soft)" stroke="var(--accent)" strokeOpacity="0.5" />
      {center.map((t, i) => (
        <text key={t} x={cx} y={cy - 4 + i * 16 - (center.length - 1) * 4} textAnchor="middle" fontSize="13" fontWeight="600" fill="var(--accent)" fontFamily="var(--font-sans)">{t}</text>
      ))}
      <text x={cx} y={cy + 30} textAnchor="middle" fontSize="10" fill="var(--text-3)" fontFamily="var(--font-mono)">drives supply</text>
    </svg>
  );
}

/** Linear provider flywheel (§48): More complete data → discoverability → query wins → revenue. */
export function ProviderFlywheel({ className }: { className?: string }) {
  const items = ["More complete data", "More discoverability", "More query wins", "More revenue"];
  return (
    <ol className={`flex flex-wrap items-center gap-2 text-sm ${className ?? ""}`} aria-label="Provider economic flywheel">
      {items.map((t, i) => (
        <li key={t} className="flex items-center gap-2">
          <span className="rounded-md border border-border-strong bg-panel-2 px-2.5 py-1.5 font-medium text-fg">{t}</span>
          {i < items.length - 1 && <span className="text-accent" aria-hidden>→</span>}
        </li>
      ))}
      <li className="text-xs text-fg-3">↺ repeat</li>
    </ol>
  );
}
